/**
 * useRawTextSearch — Hook for in-panel Ctrl+F search in raw text views.
 *
 * Manages search state, match counting, and keyboard intercept for
 * Ctrl+F / Cmd+F to activate the search bar instead of the browser default.
 */

import { useCallback, useEffect,useMemo, useRef, useState } from 'react';

export interface RawTextSearchState {
  query: string;
  setQuery: (q: string) => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  matchCount: number;
  currentIndex: number;
  goToNext: () => void;
  goToPrev: () => void;
  /** Attach to the container div to intercept Ctrl+F */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Ref to the search input for auto-focus */
  searchInputRef: React.RefObject<HTMLInputElement>;
}

export function useRawTextSearch(text: string): RawTextSearchState {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null!);
  const searchInputRef = useRef<HTMLInputElement>(null!);

  // Debounce the search query to avoid lag on large inputs
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  const matchCount = useMemo(() => {
    if (!debouncedQuery.trim()) return 0;
    const lower = text.toLowerCase();
    const needle = debouncedQuery.toLowerCase();
    let count = 0;
    let pos = 0;
    while ((pos = lower.indexOf(needle, pos)) !== -1) {
      count++;
      pos += needle.length;
    }
    return count;
  }, [text, debouncedQuery]);

  // Reset current index on query/match change
  useEffect(() => {
    setCurrentIndex(matchCount > 0 ? 0 : 0);
  }, [debouncedQuery, matchCount]);

  const open = useCallback(() => {
    setIsOpen(true);
    // Focus the search input after state update
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, close, open]);

  const goToNext = useCallback(() => {
    if (matchCount <= 0) return;
    setCurrentIndex(prev => (prev + 1) % matchCount);
  }, [matchCount]);

  const goToPrev = useCallback(() => {
    if (matchCount <= 0) return;
    setCurrentIndex(prev => (prev - 1 + matchCount) % matchCount);
  }, [matchCount]);

  // Intercept Ctrl+F / Cmd+F on the container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key === 'f') {
        e.preventDefault();
        e.stopPropagation();
        if (isOpen) {
          // Already open: focus the search input
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        } else {
          open();
        }
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        close();
      }
    };

    el.addEventListener('keydown', handler, true);
    return () => el.removeEventListener('keydown', handler, true);
  }, [isOpen, open, close]);

  return {
    query,
    setQuery,
    isOpen,
    open,
    close,
    toggle,
    matchCount,
    currentIndex,
    goToNext,
    goToPrev,
    containerRef,
    searchInputRef,
  };
}
